import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, setDoc, serverTimestamp } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from '../lib/firebase';
import { Customer } from '../types/customer';
import toast from 'react-hot-toast';
import {
  UserPlusIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon,
  SunIcon,
  MoonIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';
import Logo from '../components/icons/Logo';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';

const CustomerSignup: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { language, setLanguage } = useLanguage();
  
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    if (!token) {
      setError('Érvénytelen regisztrációs link');
      setLoading(false);
      return;
    }

    try {
      // Find customer by registration token
      const customersRef = collection(db, 'customers');
      const q = query(customersRef, where('registrationToken', '==', token));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setError('Érvénytelen vagy lejárt regisztrációs link');
        setLoading(false);
        return;
      }

      const customerDoc = snapshot.docs[0];
      const customerData = {
        id: customerDoc.id,
        ...customerDoc.data(),
        createdAt: customerDoc.data().createdAt?.toDate(),
        updatedAt: customerDoc.data().updatedAt?.toDate(),
        registrationTokenExpiry: customerDoc.data().registrationTokenExpiry?.toDate()
      } as Customer;

      // Check if token is expired
      if (customerData.registrationTokenExpiry && customerData.registrationTokenExpiry < new Date()) {
        setError('A regisztrációs link lejárt. Kérj új linket az adminisztrátortól.');
        setLoading(false);
        return;
      }

      // Check if already has user account
      if (customerData.hasUserAccount) {
        setError('Már rendelkezel felhasználói fiókkal. Jelentkezz be!');
        setLoading(false);
        return;
      }

      setCustomer(customerData);
    } catch (err) {
      console.error('Error validating token:', err);
      setError('Hiba történt a regisztrációs link ellenőrzése során');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!customer) return;

    // Validate password
    if (password.length < 6) {
      toast.error('A jelszónak legalább 6 karakter hosszúnak kell lennie');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('A jelszavak nem egyeznek');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        customer.email,
        password
      );

      const userId = userCredential.user.uid;

      // Create user document in Firestore
      await setDoc(doc(db, 'users', userId), {
        email: customer.email,
        displayName: customer.type === 'organization' 
          ? customer.companyName 
          : `${customer.lastName} ${customer.firstName}`,
        role: 'customer',
        organizationId: customer.organizations[0] || '', // First organization
        customerId: customer.id,
        photoURL: '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastLoginAt: serverTimestamp()
      });

      // Update customer record
      await updateDoc(doc(db, 'customers', customer.id), {
        userId: userId,
        hasUserAccount: true,
        registrationToken: null, // Clear token after use
        registrationTokenExpiry: null,
        updatedAt: serverTimestamp()
      });

      setSuccess(true);
      toast.success('Sikeres regisztráció! Átirányítás...');
      
      // Redirect to customer portal after 2 seconds
      setTimeout(() => {
        navigate('/customer-portal');
      }, 2000);

    } catch (err: any) {
      console.error('Error creating user:', err);
      
      if (err.code === 'auth/email-already-in-use') {
        toast.error('Ez az email cím már használatban van');
      } else {
        toast.error('Hiba történt a regisztráció során');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen relative bg-gray-100 dark:bg-gray-900 flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-100 via-amber-50 to-blue-100 dark:from-black dark:via-indigo-950 dark:to-blue-950 animate-gradient-slow">
            <div className="absolute inset-0 bg-gradient-to-tl from-purple-100/60 via-orange-100/40 to-indigo-100/60 dark:from-purple-950/60 dark:via-orange-950/50 dark:to-indigo-950/60 animate-gradient-slower" />
          </div>
        </div>
        <div className="relative animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
          <div className="text-center">
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
            <h2 className="mt-4 text-xl font-bold text-gray-900 dark:text-gray-100">
              Hiba
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {error}
            </p>
            <button
              onClick={() => navigate('/login')}
              className="mt-6 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            >
              Bejelentkezés
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8">
          <div className="text-center">
            <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
            <h2 className="mt-4 text-xl font-bold text-gray-900 dark:text-gray-100">
              Sikeres regisztráció!
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Fiókod sikeresen létrejött. Átirányítás az ügyfélportálra...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative bg-gray-100 dark:bg-gray-900 flex items-center justify-center overflow-hidden">
      {/* Theme and Language Selectors */}
      <div className="absolute top-4 right-4 z-20 flex items-center space-x-3">
        {/* Theme Selector */}
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-200 shadow-md"
          aria-label={theme === 'light' ? 'Váltás sötét módra' : 'Váltás világos módra'}
        >
          {theme === 'light' ? (
            <MoonIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          ) : (
            <SunIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          )}
        </button>

        {/* Language Selector */}
        <button
          onClick={() => setLanguage(language === 'hu' ? 'en' : 'hu')}
          className="p-2 rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-200 shadow-md flex items-center space-x-1"
          aria-label={language === 'hu' ? 'Switch to English' : 'Váltás magyarra'}
        >
          <GlobeAltIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 uppercase">
            {language === 'hu' ? 'EN' : 'HU'}
          </span>
        </button>
      </div>

      {/* Professional minimal background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-100 via-amber-50 to-blue-100 dark:from-black dark:via-indigo-950 dark:to-blue-950 animate-gradient-slow">
          <div className="absolute inset-0 bg-gradient-to-tl from-purple-100/60 via-orange-100/40 to-indigo-100/60 dark:from-purple-950/60 dark:via-orange-950/50 dark:to-indigo-950/60 animate-gradient-slower" />
          <div className="absolute inset-0 bg-gradient-to-tr from-yellow-100/30 via-transparent to-blue-100/40 dark:from-yellow-600/30 dark:via-transparent dark:to-blue-950/40 animate-gradient-medium" />
          <div className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Ccircle cx='7' cy='7' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
      </div>

      {/* Custom animations */}
      <style>{`
        @keyframes gradient-slow {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        @keyframes gradient-slower {
          0%, 100% { background-position: 100% 50%; }
          50% { background-position: 0% 50%; }
        }
        @keyframes gradient-medium {
          0%, 100% { background-position: 50% 0%; }
          50% { background-position: 50% 100%; }
        }
        .animate-gradient-slow {
          background-size: 200% 200%;
          animation: gradient-slow 15s ease infinite;
        }
        .animate-gradient-slower {
          background-size: 200% 200%;
          animation: gradient-slower 20s ease infinite;
        }
        .animate-gradient-medium {
          background-size: 200% 200%;
          animation: gradient-medium 12s ease infinite;
        }
      `}</style>

      {/* Login container */}
      <div className="relative max-w-md w-full m-4">
        <div className="bg-white/98 dark:bg-gray-900/95 backdrop-blur-md p-8 rounded-2xl shadow-2xl border border-white/20 dark:border-indigo-500/30 ring-1 ring-black/5 dark:ring-indigo-400/20">
          {/* Logo */}
          <div className="text-center mb-8">
            <Logo className="h-16 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Ügyfél Regisztráció
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Hozd létre a jelszavadat a fiókodhoz
            </p>
          </div>

        {/* Customer Info */}
        <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
            Fiók adatok
          </h3>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            <strong>Név:</strong>{' '}
            {customer?.type === 'organization'
              ? customer.companyName
              : `${customer?.lastName} ${customer?.firstName}`}
          </p>
          <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
            <strong>Email:</strong> {customer?.email}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Jelszó *
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                placeholder="Minimum 6 karakter"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5" />
                ) : (
                  <EyeIcon className="h-5 w-5" />
                )}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Jelszó megerősítése *
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={6}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
              placeholder="Jelszó újra"
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Regisztráció...
              </>
            ) : (
              <>
                <UserPlusIcon className="h-5 w-5 mr-2" />
                Fiók létrehozása
              </>
            )}
          </button>
        </form>

        {/* Login Link */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Már van fiókod?{' '}
            <button
              onClick={() => navigate('/login')}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              Bejelentkezés
            </button>
          </p>
        </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerSignup;
